import React, { useEffect, useMemo, useState } from 'react';
import { TextInput } from './common/TextInput';
import { ButtonGroup } from './common/ButtonGroup';
import { Select } from './common/LeboncoinSelect';
import {
  shippingRatesByFromLocation,
  getShippingServicesForPackage,
} from '../../common/shipping';
import type { ShippingService, ShippingRate, OriginShippingCountries } from '../../common/shipping/shippingRates.types';
import { Toggle } from './common/Toggle';
import { SupplierName } from '../../common/suppliersConfig';

const ORDER_PLATFORMS = [
  { identifier: 'ebay', label: 'eBay' },
  { identifier: 'leboncoin', label: 'Leboncoin' },
  { identifier: 'temu', label: 'Temu' },
  { identifier: 'aliexpress', label: 'Aliexpress' },
] as const;

const ORIGIN_COUNTRIES: { identifier: OriginShippingCountries; label: string }[] = [
  { identifier: 'FR', label: 'France' },
];

const DESTINATION_COUNTRIES = [
  'FR', 'DE', 'ES', 'IT', 'BE', 'LU', 'NL', 'PT', 'AT', 'IE',
].map((code) => ({ identifier: code, label: code }));

function getWeightIncrements(maxWeightGrams: number): number[] {
  const weights: number[] = [];
  weights.push(0, 500, 1000);
  for (let kg = 2; kg * 1000 <= maxWeightGrams; kg++) {
    weights.push(kg * 1000);
  }
  return weights;
}

function getMaxWeightFromService(service: ShippingService): number {
  let max = 0;
  for (const block of service.ratesByDestination) {
    for (const rate of block.rates) {
      if (rate.maxWeight > max) max = rate.maxWeight;
    }
  }
  return max;
}

function getRateForWeight(
  service: ShippingService,
  destination: string,
  weightGrams: number
): ShippingRate | null {
  const block = service.ratesByDestination.find((b) =>
    b.destinations.includes(destination)
  );
  if (!block) return null;
  return (
    block.rates.find(
      (r) => r.minWeight <= weightGrams && r.maxWeight >= weightGrams
    ) ?? null
  );
}

function getAllDestinationsFromService(service: ShippingService): string[] {
  const destinations: string[] = [];
  for (const block of service.ratesByDestination) {
    destinations.push(...block.destinations);
  }
  return destinations;
}

const ShippingRatesPage: React.FC = () => {
  const [size1, setSize1] = useState('');
  const [size2, setSize2] = useState('');
  const [size3, setSize3] = useState('');
  const [weight, setWeight] = useState('');
  const [orderPlatform, setOrderPlatform] = useState<string>('ebay');
  const [originCountry, setOriginCountry] = useState<OriginShippingCountries>('FR');
  const [destinationCountry, setDestinationCountry] = useState('FR');
  const [selectedServiceCode, setSelectedServiceCode] = useState<string | null>(null);
  const [showWithTaxPrice, setShowWithTaxPrice] = useState(false);
  
  const packageWeightGrams = parseFloat(weight) * 1000 || 0;
  const volumetricWeightGrams =
    (parseFloat(size1) * parseFloat(size2) * parseFloat(size3) / 5000) * 1000 || 0;

  const availableServices = useMemo((): ShippingService[] => {
    const s1 = parseFloat(size1);
    const s2 = parseFloat(size2);
    const s3 = parseFloat(size3);
    const w = parseFloat(weight);
    if (!s1 || !s2 || !s3 || !w || !destinationCountry) return [];

    const services = shippingRatesByFromLocation[originCountry].filter(
      (service) => {
        if (
          service.conditions?.orderPlatform &&
          service.conditions.orderPlatform !== orderPlatform
        ) {
          return false;
        }
        const dimCheck = service.maxDimensions(s1, s2, s3, w);
        if (!dimCheck.isValid) return false;
        return service.ratesByDestination.some((b) =>
          b.destinations.includes(destinationCountry)
        );
      }
    );

    return services;
  }, [
    size1,
    size2,
    size3,
    weight,
    orderPlatform,
    originCountry,
    destinationCountry,
  ]);

  const ratesForInput = useMemo(() => {
    const s1 = parseInt(size1);
    const s2 = parseInt(size2);
    const s3 = parseInt(size3);
    const w = parseInt(weight);
    if (!s1 || !s2 || !s3 || !w || !destinationCountry) return [];

    const services = getShippingServicesForPackage({
      packageWeight: w,
      packageSize1: s1,
      packageSize2: s2,
      packageSize3: s3,
      orderPlatform,
      supplier: 'FR_ilyes' as SupplierName,
      origin: originCountry,
      destination: destinationCountry,
    });
    console.log('services', services);
    return services;
  }, [
    size1,
    size2,
    size3,
    weight,
    orderPlatform,
    originCountry,
    destinationCountry,
  ]);

  const selectedService = useMemo((): ShippingService | null => {
    if (!selectedServiceCode) return null;
    return shippingRatesByFromLocation[originCountry].find((s) => s.serviceCode === selectedServiceCode) ?? null;
  }, [selectedServiceCode, originCountry]);


console.log('selectedService', selectedService);
  const rateTableData = useMemo(() => {
    if (!selectedService) return null;

    const destinations = getAllDestinationsFromService(selectedService);
    const maxWeight = getMaxWeightFromService(selectedService);
    const weightIncrements = getWeightIncrements(maxWeight);
    const useVolumetric = selectedService.appliesVolumetricWeight;

    return {
      service: selectedService,
      destinations,
      weightIncrements,
      useVolumetric,
    };
  }, [selectedService]);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);

  const formatWeight = (grams: number) => {
    if (grams < 1000) return `${grams}g`;
    return `${grams / 1000} kg`;
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Shipping rates
      </h1>

      <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Package details
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <TextInput
            label="Size 1 (cm)"
            type="number"
            value={size1}
            onChange={(e) => setSize1(e.target.value)}
            placeholder="Length"
          />
          <TextInput
            label="Size 2 (cm)"
            type="number"
            value={size2}
            onChange={(e) => setSize2(e.target.value)}
            placeholder="Width"
          />
          <TextInput
            label="Size 3 (cm)"
            type="number"
            value={size3}
            onChange={(e) => setSize3(e.target.value)}
            placeholder="Height"
          />
          <TextInput
            label="Weight (grams)"
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="0"
          />
          <div>
            <Select
              label="Order platform"
              options={ORDER_PLATFORMS.map((p) => ({
                identifier: p.identifier,
                label: p.label,
              }))}
              value={orderPlatform}
              onChange={(v) => setOrderPlatform(v)}
            />
          </div>
          <div>
            <Select
              label="Origin country"
              options={ORIGIN_COUNTRIES.map((c) => ({
                identifier: c.identifier,
                label: c.label,
              }))}
              value={originCountry}
              onChange={(v) => setOriginCountry(v as OriginShippingCountries)}
            />
          </div>
          <div>
            <Select
              label="Destination country"
              options={DESTINATION_COUNTRIES}
              value={destinationCountry}
              onChange={setDestinationCountry}
            />
          </div>
        </div>
      </div>

      {availableServices.length > 0 && (
        <>
          {ratesForInput.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-3">
                Rates for your package ({formatWeight(packageWeightGrams)} to{' '}
                {destinationCountry})
              </h2>
              <div className="space-y-2">
                {ratesForInput.map(({ service, rate }) => (
                  <div
                    key={service.serviceCode}
                    className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0"
                  >
                    <span className="font-medium">
                      {service.carrier.name} – {service.serviceCode}
                    </span>
                    <span className="text-indigo-600 font-semibold">
                      {formatPrice(rate.priceWithVAT)} (excl. VAT:{' '}
                      {formatPrice(rate.priceWithoutVAT)})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <div className="mb-4">
        <ButtonGroup
          label="Shipping services"
          options={shippingRatesByFromLocation[originCountry].map((s) => ({
            value: s.serviceCode,
            label: `${s.carrier.name} (${s.serviceCode})`,
          }))}
          value={selectedServiceCode ?? undefined}
          onChange={(v) => setSelectedServiceCode(v)}
        />
        <Toggle
          label="Show with tax price"
          value={showWithTaxPrice}
          onChange={(v) => setShowWithTaxPrice(v)}
        />
      </div>
      {selectedService && rateTableData && (
        <div className="bg-white rounded-lg shadow-lg p-4 overflow-x-auto">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">
            Full rate table – {selectedService.carrier.name}
          </h2>
          <table className="min-w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700">
                  Destination
                </th>
                {rateTableData.weightIncrements.map((w) => (
                  <th
                    key={w}
                    className="border border-gray-300 px-2 py-2 text-center text-sm font-semibold text-gray-700"
                  >
                    {formatWeight(w)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rateTableData.destinations.map((dest) => (
                <tr key={dest} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2 font-medium text-gray-900">
                    {dest}
                  </td>
                  {rateTableData.weightIncrements.map((wGrams) => {
                    const rate = getRateForWeight(
                      selectedService,
                      dest,
                      wGrams
                    );
                    return (
                      <td
                        key={wGrams}
                        className="border border-gray-300 px-2 py-2 text-center text-sm text-gray-700"
                      >
                        {rate
                          ? formatPrice(showWithTaxPrice ? rate.priceWithVAT : rate.priceWithoutVAT)
                          : '–'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {rateTableData.useVolumetric && (
            <p className="mt-2 text-sm text-gray-500">
              This service uses volumetric weight. Rates shown assume the
              volumetric weight equals the weight in the column for display
              purposes.
            </p>
          )}
        </div>
      )}

      {size1 && size2 && size3 && weight && destinationCountry && availableServices.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          No shipping services match your criteria (dimensions, platform,
          origin, destination).
        </div>
      )}
    </div>
  );
};

export default ShippingRatesPage;
